// src/components/.../Footer.jsx
import React, { useEffect, useState } from "react";
import logo from "../../../assets/images/logo.png";
import { useTranslation } from "react-i18next";
import { sendContactMessage } from "src/redux/authSlice";
import { fetchOrganizationProfile } from "src/redux/organizationSlice";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaGlobe } from "react-icons/fa";

import facebookIcon from "../../../images/facebook.png";
import instagramIcon from "../../../images/instagram.png";

// Fallback shown before the organization profile has loaded (or if the request fails),
// so the footer never renders empty. Overridden as soon as live data arrives.
const FALLBACK_PHONES = ["+216 53 27 82 86", "+33 7 53 15 72 94", "+966 55 25 26 767"];
const FALLBACK_EMAILS = ["cliclaim@cliclaim.com"];
const FALLBACK_ADDRESSES = [
  { _id: "tn", city: "Tunis", country: "Tunisie", addressLine: "15 Chedli Gtari, El Menzah 9, Tunis 1013" },
  { _id: "sa", city: "Riyad", country: "Arabie Saoudite", addressLine: "Al Miqdad, Al Rafiah, Shumaisi, Riyad 11373" }
];
const FALLBACK_SOCIAL_LINKS = [
  { _id: "fb", platform: "facebook", url: "https://www.facebook.com/profile.php?id=61588588681819" },
  { _id: "ig", platform: "instagram", url: "https://www.instagram.com/cli_claim/" },
  { _id: "tk", platform: "tiktok", url: "https://www.tiktok.com/@cliclaim" }
];

// Known platforms render a bundled brand icon — only "other" uses an admin-uploaded icon.
// Facebook/Instagram use their real (multi-color) logo images. The rest are rendered as
// inline SVGs (path data from simple-icons) filled with their official brand color — a CDN
// <img> + CSS invert() filter was used before, which turned every icon plain white instead
// of its real brand color. TikTok and X are genuinely monochrome brands: white is correct
// for them on a dark background, not a bug.
const SOCIAL_PLATFORM_META = {
  facebook: { label: "Facebook", type: "image", icon: facebookIcon },
  instagram: { label: "Instagram", type: "image", icon: instagramIcon },
  tiktok: {
    label: "TikTok",
    type: "svg",
    color: "#FFFFFF",
    path: "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.58.07-5.36.01-4.03-.01-8.05.02-12.07Z"
  },
  linkedin: {
    label: "LinkedIn",
    type: "svg",
    color: "#0A66C2",
    path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
  },
  twitter: {
    label: "X",
    type: "svg",
    color: "#FFFFFF",
    path: "M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"
  },
  youtube: {
    label: "YouTube",
    type: "svg",
    color: "#FF0000",
    path: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
  },
  whatsapp: {
    label: "WhatsApp",
    type: "svg",
    color: "#25D366",
    path: "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z M12.017 2c5.514 0 9.988 4.474 9.988 9.988a9.955 9.955 0 0 1-1.32 4.958L21 22.5l-5.65-1.652a9.958 9.958 0 0 1-3.333.577h-.004c-5.514 0-9.988-4.475-9.988-9.99A9.955 9.955 0 0 1 12.017 2Zm-.005 1.815a8.15 8.15 0 0 0-8.16 8.174 8.14 8.14 0 0 0 1.283 4.398l.2.318-.755 2.756 2.826-.741.307.183a8.13 8.13 0 0 0 4.298 1.23h.004a8.15 8.15 0 0 0 8.16-8.173 8.117 8.117 0 0 0-2.386-5.782 8.117 8.117 0 0 0-5.777-2.363Z"
  }
};

function Footer() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith("ar");
  const today = new Date();
  const dispatch = useDispatch();
  const { contactLoading } = useSelector((state) => state.auth || state.client || {});
  const organizationProfile = useSelector((state) => state.organization?.profile);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    dispatch(fetchOrganizationProfile());
  }, [dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !message) return;
    dispatch(sendContactMessage({ email, message }));
    setEmail("");
    setMessage("");
  };

  const phones = organizationProfile?.phones?.length
    ? organizationProfile.phones.map((p) => p.number)
    : FALLBACK_PHONES;
  const emails = organizationProfile?.emails?.length
    ? organizationProfile.emails.map((e) => e.email)
    : FALLBACK_EMAILS;
  const addresses = organizationProfile?.addresses?.length ? organizationProfile.addresses : FALLBACK_ADDRESSES;
  const socialLinks = organizationProfile?.socialLinks?.length
    ? organizationProfile.socialLinks
    : FALLBACK_SOCIAL_LINKS;

  return (
    <footer className="footer footercx" style={{ padding: "10px 0", zIndex: 999 }}>
      <section>
        <div className="container mt-3">
          <div className="mt-3 row gy-2 gy-md-3 justify-content-between">

            {/* ── Contact Us ── */}
<div className="col-12 col-md-4 col-lg-3 col-xl-3" style={{ marginBottom: '24px' }}>
              <div className="widget">
                <h4 className="widget-title text-white mb-3" style={{ fontSize: "16px", fontWeight: "bold" }}>
                  {t("Contact Us")}
                </h4>

                {phones.map((phone) => (
                  <p className="mb-2 d-flex align-items-center gap-2" key={phone}>
                    <FaPhone style={{ color: "#1fc17d", transform: "scaleX(-1)", flexShrink: 0, fontSize: "14px" }} />
                    <a
                      className="text-decoration-none text-white"
                      href={`tel:${phone.replace(/[^+\d]/g, "")}`}
                      style={{ fontSize: "13px" }}
                    >
                      {phone}
                    </a>
                  </p>
                ))}

                {emails.map((emailAddress) => (
                  <p className="mb-2 d-flex align-items-center gap-2" key={emailAddress}>
                    <FaEnvelope style={{ color: "#1fc17d", flexShrink: 0, fontSize: "14px" }} />
                    <a className="text-decoration-none text-white" href={`mailto:${emailAddress}`} style={{ fontSize: "13px" }}>
                      {emailAddress}
                    </a>
                  </p>
                ))}

                {addresses.map((address) => {
                  // Arabic fields are auto-translated server-side when the address is
                  // saved in the super admin — fall back to the French/original text if
                  // a translation is missing (e.g. Google Translate was unavailable).
                  const city = (isArabic && address.city_ar) || address.city;
                  const country = (isArabic && address.country_ar) || address.country;
                  const addressLine = (isArabic && address.addressLine_ar) || address.addressLine;
                  return (
                    <div className="mb-1 d-flex align-items-start gap-2" key={address._id}>
                      <FaMapMarkerAlt style={{ color: "#1fc17d", flexShrink: 0, fontSize: "14px", marginTop: "3px" }} />
                      <div>
                        <p className="mb-0 text-white" style={{ fontSize: "12px", fontWeight: "600" }}>
                          {city}, {country}
                        </p>
                        <a
                          className="text-decoration-none text-white"
                          href={`https://maps.google.com/?q=${encodeURIComponent(address.addressLine)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: "13px" }}
                        >
                          {addressLine}
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

{/* ── Follow Us ── */}
<div className="col-12 col-md-4 col-lg-3 col-xl-2" style={{ marginBottom: '24px' }}>
  <div className="widget">
    <h4 className="widget-title text-white mb-3" style={{ fontSize: "16px", fontWeight: "bold", textAlign: "left" }}>
      {t("Follow us")}
    </h4>

    <div style={{ display: "flex", flexDirection: "column", gap: "16px", alignItems: "flex-start" }}>
      {socialLinks.map((link) => {
        const meta = SOCIAL_PLATFORM_META[link.platform];
        const label = meta?.label || link.label || link.platform;
        const customIconSrc =
          !meta && link.icon ? `${process.env.REACT_APP_BACKEND_URL}/organization/social-links/icon/${link.icon}` : null;

        let iconEl;
        if (meta?.type === "image") {
          iconEl = (
            <img src={meta.icon} alt={label} style={{ width: "20px", height: "20px", objectFit: "contain" }} />
          );
        } else if (meta?.type === "svg") {
          iconEl = (
            <svg width="20" height="20" viewBox="0 0 24 24" fill={meta.color}>
              <path d={meta.path} />
            </svg>
          );
        } else if (customIconSrc) {
          iconEl = (
            <img src={customIconSrc} alt={label} style={{ width: "20px", height: "20px", objectFit: "contain" }} />
          );
        } else {
          iconEl = <FaGlobe style={{ width: "20px", height: "20px", color: "#cfcfcf" }} />;
        }

        return (
          <a
            key={link._id}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: "flex", alignItems: "center", gap: "10px", textDecoration: "none", color: "#cfcfcf", fontSize: "13px" }}
          >
            {iconEl}
            <span>{label}</span>
          </a>
        );
      })}
    </div>
  </div>
</div>

            {/* ── Learn More ── */}
<div className="col-12 col-md-4 col-lg-3 col-xl-2" style={{ marginBottom: '24px' }}>
              <div className="widget">
                <h4 className="widget-title text-white mb-3" style={{ fontSize: "16px", fontWeight: "bold" }}>
                  {t("Learn More")}
                </h4>
                <ul className="list-unstyled">
                  <li className="mb-2">
                    <Link to="/about" className="text-decoration-none text-white" style={{ fontSize: "13px" }}>{t("nav.about")}</Link>
                  </li>
                  <li className="mb-2">
                    <Link to="/terms" className="text-decoration-none text-white" style={{ fontSize: "13px" }}>{t("nav.terms")}</Link>
                  </li>
                  <li className="mb-0">
                    <Link to="/privacy" className="text-decoration-none text-white" style={{ fontSize: "13px" }}>{t("nav.privacy")}</Link>
                  </li>
                </ul>
              </div>
            </div>

            {/* ── Formulaire Contact ── */}
            <div className="col-12 col-lg-3 col-xl-4">
              <div className="widget">
                <h4 className="widget-title text-white mb-3" style={{ fontSize: "16px", fontWeight: "bold" }}>
                  {t("Contact")}
                </h4>
                <p className="mb-2 text-white" style={{ fontSize: "13px" }}>
                  {t("Leave your message.")}
                </p>
                <form onSubmit={handleSubmit}>
                  <div className="row gy-2">
                    <div className="col-12">
                      <input
                        type="email"
                        className="w-100 input_login"
                        placeholder={t("Email Address")}
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={{ color: "#fff", fontWeight: "bold", fontSize: "12px" }}
                      />
                    </div>
                    <div className="col-12">
                      <textarea
                        className="w-100 input_login"
                        placeholder={t("Your Message")}
                        required
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        style={{
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: "12px",
                          height: 60,
                          resize: "none",
                          paddingTop: "10px",
                          borderRadius: "8px",
                          display: "block",
                        }}
                      />
                    </div>
                    <div className="col-12 mt-2">
                      <button className="order_online3 w-100" type="submit" disabled={contactLoading}>
                        {contactLoading ? t("Sending...") : t("Send")}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Copyright ── */}
      <div
        className="container-fluid"
        style={{ borderTop: "1px solid rgba(255, 255, 255, 0.08)", marginTop: "20px" }}
      >
        <div className="d-flex justify-content-center align-items-center" style={{ padding: "14px 0" }}>
          <span style={{ fontSize: "13px", color: "rgba(255,255,255,0.5)" }}>
            <span style={{ color: "#18e68e" }}>C</span>li
            <span style={{ color: "#18e68e" }}>C</span>laim&nbsp;
            © {today?.getFullYear()}&nbsp;
            {t("All rights reserved.")}
          </span>
        </div>
      </div>

    </footer>
  );
}

export default Footer;
